/* eslint-disable react/jsx-props-no-spreading,react/require-default-props */
import React, { useCallback, useEffect, useState } from 'react';
import { Input } from 'antd';
import PropTypes from 'prop-types';
import { moneyFormat } from '../../../services/util';

/**
 * @TODO: Criar componentes mais específicos para melhorar a semântica.
 * OBS: Os componentes criados podem extender o Input base,
 * mas o Input base não deveria ser utilizado.
 *
 * Isso vai evitar a passagem de parâmetros de configuração e facilita
 * a compreensão do propósito da utilização do componente no código.
 *
 * e.g. Utilizar componente InputMoney, InputPercentage (já criado):
 * <InputPercentage value={value} onChange={handleOnChange} />
 * ao invés de:
 * <CustomInput suffix="%" casasDecimais={0} value={value} onChange={handleOnChange}
 */
function CustomInput({
  value,
  prefix,
  getValue,
  autoSelect,
  disabled,
  style,
  onBlur,
  min,
  max,
  casasDecimais,
  type,
  id,
  size,
  maxLength,
  defaultValue,
  className,
  addonBefore,
  addonAfter,
  prefixCls,
  onPressEnter,
  onKeyDown,
  onKeyUp,
  onFocus,
  suffix,
  allowClear,
  autoFocus,
}) {
  const formatoCasasDecimais = {
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais,
  };

  const valorZeradoFormatado = moneyFormat(0, 'pt-br', formatoCasasDecimais);
  const [viewValue, setViewValue] = useState(valorZeradoFormatado);

  useEffect(() => {
    if (value === null || value === undefined) {
      setViewValue(valorZeradoFormatado);
    } else {
      setViewValue(moneyFormat(value, 'pt-br', formatoCasasDecimais));
    }
  }, [value, formatoCasasDecimais]);

  const onChange = ({ target: { value: valor } }) => {
    const valorSemPontos = valor.replace(/\D/g, '');
    let valorCampo = 0;

    let divisor = `${1}`;

    for (let i = 0; i < casasDecimais; i += 1) {
      divisor += `${0}`;
    }
    valorCampo = valorSemPontos / divisor;

    if (valorCampo > max) {
      valorCampo = max;
    }
    if (valorCampo < min) {
      valorCampo = min;
    }
    setViewValue(moneyFormat(valorCampo, 'pt-br', formatoCasasDecimais));
    getValue(valorCampo);
  };

  const focusInput = useCallback(
    ({ target }) => {
      if (autoSelect) {
        target.setSelectionRange(0, target.value.length);
      }

      if (onFocus) {
        onFocus();
      }
    },
    [autoSelect]
  );

  return (
    <Input
      data-testid={id}
      value={viewValue}
      onFocus={focusInput}
      onChange={onChange}
      prefix={prefix}
      style={style}
      disabled={disabled}
      onBlur={onBlur}
      type={type}
      id={id}
      size={size}
      maxLength={maxLength}
      defaultValue={defaultValue}
      className={className}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      prefixCls={prefixCls}
      onPressEnter={onPressEnter}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      suffix={suffix}
      allowClear={allowClear}
      autoFocus={autoFocus}
    />
  );
}

CustomInput.defaultProps = {
  getValue: (value) => value,
  value: 0,
  autoSelect: false,
  disabled: false,
  style: {},
  onBlur: (e) => e,
  min: 0,
  max: 999999,
  casasDecimais: 2,
};

CustomInput.propTypes = {
  getValue: PropTypes.func,
  value: PropTypes.number,
  prefix: PropTypes.string,
  autoSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.shape(PropTypes.object.isRequired),
  onBlur: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  casasDecimais: PropTypes.number,

  type: PropTypes.string,
  id: PropTypes.string,
  size: PropTypes.oneOf(['small', 'default', 'large']),
  maxLength: PropTypes.number,
  defaultValue: PropTypes.shape(PropTypes.any),
  className: PropTypes.string,
  addonBefore: PropTypes.node,
  addonAfter: PropTypes.node,
  prefixCls: PropTypes.string,
  onPressEnter: PropTypes.func,
  onKeyDown: PropTypes.func,
  autoFocus: PropTypes.func,
  onKeyUp: PropTypes.func,
  onFocus: PropTypes.func,
  suffix: PropTypes.node,
  allowClear: PropTypes.bool,
};

export default CustomInput;
